// ADR 16.9.26r §1 — context is published, not fetched.
//
// The phones' `TutorContextHolder` (15.8.26g D4): a last-writer-wins holder
// that each screen publishes into on enter and clears on leave, read at
// send time by the panel. One React context, mounted once by `AdaptiveFrame`
// beside the dock's, and one hook for the pages — which do not know the tutor
// exists; they publish what they show, built through rust-core's own builder
// so the serialised keys are the phones' (9.9.26u §2).
//
// §3 — while the dock is `detached`, every publish is mirrored onto the
// pop-out channel, and `hello` is answered at once with the current value:
// the context the learner is sitting on was published before the popup
// existed, and without the answer the popup would hold `null` until the next
// publish. The provider also posts `context null` from its own tab's
// `pagehide`, because React cleanups do not run when a tab closes and the
// popup would otherwise coach "set 2, question 3" for as long as it lived.
// In the popup itself (`/tutor?detached=1`) the roles flip: the provider
// applies every `context` it hears and mirrors nothing.
//
// ADR 30.9.26ak §4 — an app slot sits *beside* a page, so its note is a second,
// independent value, never one more last-writer on `context`. The read
// composes, and with no app it is exactly the old read (`null` included). The
// composer travels with the slice: this module is on the boot graph
// (`AdaptiveShell`), so it may not import the wasm facade (25.9.26f AC1), and
// the one publisher — the slot, on the wasm side — brings `composeAppSlice`
// (`appSlice.ts`). Mirror and `hello` post the composed value, so a detached
// tutor sees the note too.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type Dispatch, type ReactNode, type SetStateAction,
} from 'react'
import { useLocation } from 'react-router-dom'

import type { TutorAppSlice, TutorTurnContext } from '../../../wasm'
import { useTutorDock } from '../dock/TutorDockProvider'
import { isDetachedWindow, postPopout, subscribePopout, type PopoutMessage } from '../dock/popout'

/** Puts an app's slice onto a page's context (`appSlice.ts`'s `composeAppSlice`). */
export type AppComposer = (context: TutorTurnContext | null, app: TutorAppSlice) => TutorTurnContext | null

interface PublishedApp {
  slice: TutorAppSlice
  compose: AppComposer
}

interface TutorContextValue {
  context: TutorTurnContext | null
  publish(context: TutorTurnContext | null): void
  setApp: Dispatch<SetStateAction<PublishedApp | null>>
}

/** Defaulted, not throwing: the pages render without the shell in their suites. */
const TutorContextContext = createContext<TutorContextValue>({ context: null, publish: () => {}, setApp: () => {} })

/** The panel's read at send time: the page's context, with the slot's app composed on. */
export function useTutorContext(): TutorTurnContext | null {
  return useContext(TutorContextContext).context
}

/**
 * The slot's door (30.9.26ak §4), reached through `appSlice.ts`'s
 * `usePublishTutorAppSlice`. Publishes a non-null slice, and on cleanup clears
 * it **only if it is still the one stored**: a leaving slot and an arriving one
 * can run in either order, and the slice carries no id. Like
 * `usePublishTutorContext` it needs a memoised slice — a fresh object per
 * render would re-publish forever and never match `===`.
 */
export function usePublishTutorApp(slice: TutorAppSlice | null, compose: AppComposer): void {
  const { setApp } = useContext(TutorContextContext)
  useEffect(() => {
    if (slice === null) return
    setApp({ slice, compose })
    return () => setApp((prev) => (prev?.slice === slice ? null : prev))
  }, [setApp, slice, compose])
}

/**
 * The pages' one door: publishes on mount and whenever `context` changes, and
 * `null` on unmount (15.8.26g D4: symmetric). Callers pass a memoised value —
 * a fresh object per render would re-publish, and while detached re-post, on
 * every render.
 */
export function usePublishTutorContext(context: TutorTurnContext | null): void {
  const { publish } = useContext(TutorContextContext)
  useEffect(() => {
    publish(context)
    return () => publish(null)
  }, [publish, context])
}

/**
 * §3 — the mirror: every publish while `detached`, and `null` on the tab's
 * `pagehide`. Keyed on the context alone, with `detached` read through a
 * ref: the `hello` handler already answers the transition with the current
 * value, and an effect keyed on both would post it a second time.
 */
function useMirror(context: TutorTurnContext | null, detached: boolean): void {
  const detachedRef = useRef(detached)
  detachedRef.current = detached
  useEffect(() => {
    if (detachedRef.current) postPopout({ kind: 'context', context })
  }, [context])
  useEffect(() => {
    if (!detached) return
    const onPageHide = () => postPopout({ kind: 'context', context: null })
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [detached])
}

export function TutorContextProvider({ children }: { children: ReactNode }) {
  const [context, setContext] = useState<TutorTurnContext | null>(null)
  const [app, setApp] = useState<PublishedApp | null>(null)
  // No app, no wasm call: the page's value, `null` included, exactly as before.
  const composed = useMemo(() => (app === null ? context : app.compose(context, app.slice)), [context, app])
  const latest = useRef(composed)
  latest.current = composed
  const dock = useTutorDock()
  const popup = isDetachedWindow(useLocation().search)
  const detached = dock.state === 'detached'
  useMirror(composed, detached)

  const { hello, bye } = dock
  useEffect(() => {
    const onMessage = (message: PopoutMessage) => {
      if (popup) {
        if (message.kind === 'context') setContext(message.context)
        return
      }
      if (message.kind === 'hello') {
        hello()
        postPopout({ kind: 'context', context: latest.current })
      } else if (message.kind === 'bye') {
        bye()
      }
    }
    // The popup subscribes to receive; a plan tab subscribes to be told. A tab
    // on neither side still opens the channel here — one per tab, closed never,
    // which is the cost of hearing `hello` without a poll.
    return subscribePopout(onMessage)
  }, [popup, hello, bye])

  const publish = useCallback((next: TutorTurnContext | null) => setContext(next), [])
  const value = useMemo(() => ({ context: composed, publish, setApp }), [composed, publish])
  return <TutorContextContext.Provider value={value}>{children}</TutorContextContext.Provider>
}
